/**
 * Viewport Scale Utility
 *
 * Week 2: Dual Scale System
 * Calculates automatic viewport scaling to fit viewport dimensions within available canvas space
 */

export interface ViewportDimensions {
  width: number;
  height: number;
}

export interface CanvasDimensions {
  width: number;
  height: number;
}

/**
 * Calculate viewport scale to fit viewport in available canvas space
 *
 * @param canvasWidth - Available canvas container width in pixels
 * @param canvasHeight - Available canvas container height in pixels
 * @param viewportWidth - Target viewport width in pixels
 * @param viewportHeight - Target viewport height in pixels
 * @param padding - Padding factor (0.9 = 10% padding, 1.0 = no padding)
 * @returns Calculated scale factor, clamped between 0.1 and 2.0
 *
 * @example
 * // Desktop viewport (1920x1080) in canvas (1200x800)
 * calculateViewportScale(1200, 800, 1920, 1080, 0.9)
 * // Returns: 0.5625 (min(1200/1920, 800/1080) * 0.9 = min(0.625, 0.74) * 0.9)
 *
 * @example
 * // Mobile viewport (375x667) in canvas (1200x800)
 * calculateViewportScale(1200, 800, 375, 667, 0.9)
 * // Returns: 1.08 (min(1200/375, 800/667) * 0.9 = min(3.2, 1.2) * 0.9)
 */
export function calculateViewportScale(
  canvasWidth: number,
  canvasHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  padding: number = 0.9
): number {
  // Validate inputs
  if (canvasWidth <= 0 || canvasHeight <= 0) {
    console.warn('[Viewport Scale] Invalid canvas dimensions:', { canvasWidth, canvasHeight });
    return 1.0;
  }

  if (viewportWidth <= 0 || viewportHeight <= 0) {
    console.warn('[Viewport Scale] Invalid viewport dimensions:', { viewportWidth, viewportHeight });
    return 1.0;
  }

  // Calculate scale to fit viewport in canvas
  const scaleX = canvasWidth / viewportWidth;
  const scaleY = canvasHeight / viewportHeight;

  // Use the smaller scale to ensure both dimensions fit
  const scale = Math.min(scaleX, scaleY) * padding;

  // Clamp scale between 0.1 (10%) and 2.0 (200%)
  const clampedScale = Math.max(0.1, Math.min(2.0, scale));

  return clampedScale;
}

/**
 * Calculate effective scale combining viewport scale and user zoom
 *
 * @param viewportScale - Automatic viewport scale (0.1-2.0)
 * @param userZoom - Manual user zoom (0.25-4.0)
 * @returns Combined effective scale factor
 *
 * @example
 * calculateEffectiveScale(0.75, 1.5) // Returns: 1.125
 */
export function calculateEffectiveScale(
  viewportScale: number,
  userZoom: number
): number {
  return viewportScale * userZoom;
}

/**
 * Convert pixel coordinates from scaled canvas to viewport space
 *
 * @param x - X coordinate in scaled canvas space
 * @param y - Y coordinate in scaled canvas space
 * @param effectiveScale - Combined viewport scale × user zoom
 * @returns Coordinates in viewport space
 *
 * @example
 * // Click at (150, 200) on canvas with 0.75 effective scale
 * canvasToViewportCoords(150, 200, 0.75)
 * // Returns: { x: 200, y: 266.67 } (scaled up to viewport space)
 */
export function canvasToViewportCoords(
  x: number,
  y: number,
  effectiveScale: number
): { x: number; y: number } {
  return {
    x: x / effectiveScale,
    y: y / effectiveScale,
  };
}

/**
 * Convert pixel coordinates from viewport space to scaled canvas
 *
 * @param x - X coordinate in viewport space
 * @param y - Y coordinate in viewport space
 * @param effectiveScale - Combined viewport scale × user zoom
 * @returns Coordinates in scaled canvas space
 *
 * @example
 * // Component at (200, 300) in viewport with 0.75 effective scale
 * viewportToCanvasCoords(200, 300, 0.75)
 * // Returns: { x: 150, y: 225 } (scaled down to canvas space)
 */
export function viewportToCanvasCoords(
  x: number,
  y: number,
  effectiveScale: number
): { x: number; y: number } {
  return {
    x: x * effectiveScale,
    y: y * effectiveScale,
  };
}

/**
 * Format scale as percentage string for display
 *
 * @param scale - Scale factor (e.g., 0.75, 1.5, 2.0)
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted percentage string (e.g., "75%", "150%", "200%")
 *
 * @example
 * formatScaleAsPercentage(0.75) // Returns: "75%"
 * formatScaleAsPercentage(1.125, 1) // Returns: "112.5%"
 */
export function formatScaleAsPercentage(scale: number, decimals: number = 0): string {
  const percentage = scale * 100;
  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Get recommended zoom levels for zoom controls
 *
 * @returns Array of recommended zoom levels with labels
 */
export function getRecommendedZoomLevels(): Array<{ value: number; label: string }> {
  return [
    { value: 0.25, label: '25%' },
    { value: 0.5, label: '50%' },
    { value: 0.75, label: '75%' },
    { value: 1.0, label: '100%' },
    { value: 1.5, label: '150%' },
    { value: 2.0, label: '200%' },
    { value: 3.0, label: '300%' },
    { value: 4.0, label: '400%' },
  ];
}

/**
 * Debounce scale calculations to improve performance
 *
 * @param func - Function to debounce
 * @param delay - Delay in milliseconds (default: 100ms)
 * @returns Debounced function
 */
export function debounceScaleCalculation<T extends (...args: any[]) => any>(
  func: T,
  delay: number = 100
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return function debounced(...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay);
  };
}
